import { Helmet } from 'react-helmet-async';
import { FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { CheckCircleIcon, ArrowLeftIcon } from '@heroicons/react/24/solid';
import { useWhitepapers, type WhitepaperCardItem } from '@/hooks/useWhitepapers';
import Loader from '@/components/Common/Loader';
import SharedButton from '@/global/Button/Button';
import { sendGAPageview } from '../utils/gaConsent';
import './WhitepaperRequestPage.css';

const CONTACT_API_BASE_URL = process.env.REACT_APP_CONTACT_API_URL || '';

type WhitepaperLeadForm = {
  name: string;
  organisation: string;
  role: string;
  primaryInterestArea: string;
  preferredContact: string;
};

const emptyForm: WhitepaperLeadForm = {
  name: '',
  organisation: '',
  role: '',
  primaryInterestArea: '',
  preferredContact: '',
};

const whitepaperModalFieldLabelClass =
  'block text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 dark:text-slate-400';

const whitepaperModalInputClass =
  'mt-1.5 block w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-800 placeholder:text-slate-400 transition-colors focus:border-blue focus:outline-none focus:ring-2 focus:ring-blue/20 dark:border-white/15 dark:bg-slate-900 dark:text-slate-100 dark:placeholder:text-slate-500 dark:focus:ring-blue/30';

const parseWhitepaperId = (value: string | undefined): number | null => {
  if (!value) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

const WhitepaperMetadataStat = ({ value, label }: { value: string; label: string }) => (
  <div>
    <p className="text-2xl font-bold tracking-tight text-blue sm:text-2xl mb-0">{value}</p>
    <p className="mt-1 text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
      {label}
    </p>
  </div>
);

const WhitepaperRequestPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { whitepaperId: whitepaperIdParam } = useParams<{
    whitepaperId?: string;
  }>();

  const selectedWhitepaperId = useMemo(() => parseWhitepaperId(whitepaperIdParam), [whitepaperIdParam]);

  const { whitepapers, loading: whitepapersLoading, error: whitepapersError } = useWhitepapers();

  const [form, setForm] = useState<WhitepaperLeadForm>(emptyForm);
  const [formError, setFormError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [requestSuccess, setRequestSuccess] = useState(false);

  const selectedWhitepaper: WhitepaperCardItem | null = useMemo(() => {
    if (!selectedWhitepaperId) return null;
    return whitepapers.find(item => item.id === selectedWhitepaperId) || null;
  }, [selectedWhitepaperId, whitepapers]);

  const backToPublicationsPath = '/publications/whitepapers';

  useEffect(() => {
    setForm(emptyForm);
    setFormError('');
    setIsSubmitting(false);
    setRequestSuccess(false);
  }, [selectedWhitepaperId]);

  useEffect(() => {
    sendGAPageview({ hitType: 'pageview', page: location.pathname, title: 'Whitepaper request' });
  }, [location.pathname]);

  useEffect(() => {
    if (whitepapersLoading) return;

    if (!selectedWhitepaperId) {
      navigate(backToPublicationsPath, { replace: true });
      return;
    }

    const exists = whitepapers.some(item => item.id === selectedWhitepaperId);
    if (!exists) {
      navigate(backToPublicationsPath, { replace: true });
    }
  }, [navigate, selectedWhitepaperId, whitepapers, whitepapersLoading]);

  const onSubmitWhitepaperForm = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      if (!selectedWhitepaper) return;

      if (!CONTACT_API_BASE_URL) {
        setFormError('Whitepaper form backend is not configured. Please contact support.');
        return;
      }

      setIsSubmitting(true);
      setFormError('');

      try {
        const response = await fetch(`${CONTACT_API_BASE_URL}/whitepaper/request`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ...form,
            whitepaperTitle: selectedWhitepaper.title,
            pdfUrl: selectedWhitepaper.pdfUrl || selectedWhitepaper.link,
          }),
        });

        if (!response.ok) {
          let backendError = '';
          try {
            const errorPayload = await response.json();
            backendError = String(errorPayload?.error || errorPayload?.message || '');
          } catch {
            // Ignore parse error and use default message.
          }

          if (backendError.toLowerCase().includes('disposable email')) {
            throw new Error(
              'Disposable email addresses are not allowed. Please use your institutional email.',
            );
          }

          throw new Error(backendError || 'Unable to process whitepaper request');
        }

        setRequestSuccess(true);
      } catch (submissionError) {
        console.error('Whitepaper request error:', submissionError);
        setFormError(
          submissionError instanceof Error
            ? submissionError.message
            : 'We could not process your request. Please try again in a moment.',
        );
      } finally {
        setIsSubmitting(false);
      }
    },
    [form, selectedWhitepaper],
  );

  const formContent = whitepapersLoading ? (
    <div className="py-10">
      <Loader text="Loading whitepaper..." isInnerSpinner />
    </div>
  ) : requestSuccess ? (
    <div className="mt-1 bg-slate-50/90 px-6 py-8 text-center rounded-xl" role="status" aria-live="polite">
      <div className="mx-auto flex h-14 w-14 items-center justify-center">
        <CheckCircleIcon className="h-8 w-8 text-pink" aria-hidden />
      </div>
      <p className="mt-3 text-base font-semibold text-blue">Check your inbox</p>
      <p className="mx-auto mt-2 max-w-sm text-sm leading-relaxed text-slate-600 dark:text-slate-400">
        We have sent you an email with the download link.
      </p>
    </div>
  ) : selectedWhitepaper ? (
    <form className="space-y-5" onSubmit={onSubmitWhitepaperForm}>
      <div className="grid gap-4 sm:grid-cols-2">
        <label className="sm:col-span-1">
          <span className={whitepaperModalFieldLabelClass}>Name</span>
          <input
            className={whitepaperModalInputClass}
            autoComplete="name"
            placeholder="Your name"
            value={form.name}
            onChange={event => setForm(previous => ({ ...previous, name: event.target.value }))}
            required
          />
        </label>

        <label className="sm:col-span-1">
          <span className={whitepaperModalFieldLabelClass}>Organisation</span>
          <input
            className={whitepaperModalInputClass}
            autoComplete="organization"
            placeholder="Company or institution"
            value={form.organisation}
            onChange={event => setForm(previous => ({ ...previous, organisation: event.target.value }))}
            required
          />
        </label>

        <label className="sm:col-span-1">
          <span className={whitepaperModalFieldLabelClass}>Role</span>
          <input
            className={whitepaperModalInputClass}
            autoComplete="organization-title"
            placeholder="e.g. Research scientist"
            value={form.role}
            onChange={event => setForm(previous => ({ ...previous, role: event.target.value }))}
            required
          />
        </label>

        <label className="sm:col-span-1">
          <span className={whitepaperModalFieldLabelClass}>Primary interest</span>
          <input
            className={whitepaperModalInputClass}
            placeholder="Therapeutic area or use case"
            value={form.primaryInterestArea}
            onChange={event =>
              setForm(previous => ({ ...previous, primaryInterestArea: event.target.value }))
            }
            required
          />
        </label>

        <label className="sm:col-span-2">
          <span className={whitepaperModalFieldLabelClass}>Email</span>
          <input
            type="email"
            className={whitepaperModalInputClass}
            autoComplete="email"
            placeholder="you@organisation.com"
            value={form.preferredContact}
            onChange={event => setForm(previous => ({ ...previous, preferredContact: event.target.value }))}
            required
          />
        </label>
      </div>

      {formError ? (
        <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-400/35 dark:bg-red-950/40 dark:text-red-200">
          {formError}
        </p>
      ) : null}

      <div className="flex flex-col gap-3 border-t border-slate-200/80 pt-5 dark:border-white/10 sm:flex-row sm:items-center sm:justify-end">
        <SharedButton
          type="submit"
          variant="primary"
          size="sm"
          disabled={isSubmitting}
          className="ml-0 w-full sm:w-auto"
        >
          {isSubmitting ? 'Sending...' : 'Send me the PDF link'}
        </SharedButton>
      </div>
    </form>
  ) : (
    <div className="py-10">
      <Loader text="Loading whitepaper..." isInnerSpinner />
    </div>
  );

  return (
    <>
      <Helmet>
        <title>{selectedWhitepaper ? `DISGENET: ${selectedWhitepaper.title}` : 'DISGENET: Whitepaper'}</title>
        <meta name="description" content="Complete the form to request the whitepaper PDF link by email." />
        <meta
          property="og:title"
          content={selectedWhitepaper ? `DISGENET: ${selectedWhitepaper.title}` : 'DISGENET: Whitepaper'}
        />
        <meta
          property="og:description"
          content="Complete the form to request the whitepaper PDF link by email."
        />
        <meta
          property="og:url"
          content={`https://www.disgenet.com/publications/whitepapers/${whitepaperIdParam}`}
        />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="/publications" />
      </Helmet>

      <section className="landing-section landing-surface-a pt-4">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <button
            type="button"
            onClick={() => navigate(backToPublicationsPath)}
            className="inline-flex items-center gap-2 text-sm font-medium text-blue hover:text-blue/90"
          >
            <ArrowLeftIcon className="h-4 w-4" aria-hidden />
            Back to Whitepapers
          </button>

          {whitepapersError ? (
            <div className="mt-6 landing-card-dark border border-red-200/70 bg-red-50/70 p-6 text-sm text-red-700 dark:border-red-400/30 dark:bg-red-950/30 dark:text-red-300">
              Whitepapers: {whitepapersError}
            </div>
          ) : null}

          <div className="mt-6 grid gap-10 lg:grid-cols-2 lg:items-center">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-secondary">WHITEPAPER</p>
              <h1 className="mt-2 text-2xl font-bold tracking-tight text-blue sm:text-4xl lg:leading-tight">
                {selectedWhitepaper?.title || (whitepapersLoading ? 'Loading...' : 'Whitepaper')}
              </h1>
              {selectedWhitepaper?.date ? (
                <p className="mt-2 text-xs font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
                  Published · {selectedWhitepaper.date}
                </p>
              ) : null}
              {selectedWhitepaper?.descriptionHtml ? (
                <div
                  className="whitepaper-description mt-6"
                  dangerouslySetInnerHTML={{ __html: selectedWhitepaper.descriptionHtml }}
                />
              ) : selectedWhitepaper?.description ? (
                <p className="mt-8 text-slate-600 dark:text-slate-400">{selectedWhitepaper.description}</p>
              ) : null}
              {selectedWhitepaper ? (
                <div className="mt-4 max-w-md border-t border-slate-200/80 pt-4 dark:border-white/10">
                  <div className="grid grid-cols-3 gap-6 sm:gap-8">
                    <WhitepaperMetadataStat
                      value={selectedWhitepaper.pagesNumber ? String(selectedWhitepaper.pagesNumber) : '—'}
                      label="Pages"
                    />
                    <WhitepaperMetadataStat value={selectedWhitepaper.readingTime ?? '—'} label="Read time" />
                    <WhitepaperMetadataStat value="PDF" label="Format" />
                  </div>
                </div>
              ) : null}
            </div>

            <div className="flex justify-end">
              <div className="w-full max-w-lg rounded-2xl border border-slate-200/80 bg-white p-6 shadow-sm dark:border-white/10 dark:bg-slate-900/55">
                <p className="text-sm font-semibold text-slate-900 dark:text-slate-50">Get the whitepaper</p>
                <p className="mt-2 text-sm leading-relaxed text-slate-600 dark:text-slate-400">
                  Complete the form and we will send the PDF link to your email.
                </p>

                <div className="mt-5">{formContent}</div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default WhitepaperRequestPage;
